import React, { useEffect, useState } from 'react';
import { FileText, Loader2 } from 'lucide-react';
import { apiClient } from '../../api/client';

const SOURCE_STYLE = {
  footer: { bg: '#EDE9FE', fg: '#7B4FD8', label: 'Website footer' },
  education: { bg: '#DBEAFE', fg: '#2563EB', label: 'Education page' },
  dataroom: { bg: '#FEF3C7', fg: '#B45309', label: 'Data Room' },
  direct: { bg: '#F3F4F6', fg: '#44403A', label: 'Direct link' },
};

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso.slice(0, 16); }
}

const WhitePaperLedgerCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await apiClient.request('/api/admin/whitepaper-downloads');
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load ledger');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }} data-testid="whitepaper-ledger-card">
      <div className="flex items-center gap-2 mb-4">
        <FileText size={18} style={{ color: '#7B4FD8' }} />
        <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>White Paper Download Ledger</h3>
        {data && (
          <span className="ml-auto px-2 py-0.5 text-xs font-medium rounded-full" style={{ backgroundColor: '#EDE9FE', color: '#7B4FD8' }}>
            {data.last_7d} this week
          </span>
        )}
      </div>
      <p className="text-xs mb-3" style={{ color: '#666' }}>
        Everyone who gave their name + email to read the Warm Handover white paper.
      </p>

      {loading ? (
        <div className="flex justify-center py-6"><Loader2 size={20} className="animate-spin" style={{ color: '#7B4FD8' }} /></div>
      ) : error ? (
        <p className="text-xs py-2" style={{ color: '#B91C1C' }}>{error}</p>
      ) : (
        <>
          <div className="flex gap-6 mb-4">
            <div>
              <p className="text-2xl font-bold" style={{ color: '#7B4FD8' }} data-testid="whitepaper-total-downloads">{data.total}</p>
              <p className="text-xs" style={{ color: '#666' }}>Total downloads</p>
            </div>
            <div>
              <p className="text-2xl font-bold" style={{ color: '#333' }}>{data.unique_emails}</p>
              <p className="text-xs" style={{ color: '#666' }}>Unique readers</p>
            </div>
          </div>

          {data.entries.length === 0 ? (
            <p className="text-xs py-2" style={{ color: '#6B6257' }}>No downloads yet — the gate is live on the website footer, Education page and Data Room.</p>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="whitepaper-ledger-entries">
              {data.entries.map((e, i) => {
                const st = SOURCE_STYLE[e.source] || SOURCE_STYLE.direct;
                return (
                  <div key={e.download_id || i} className="flex items-center justify-between py-1.5 px-2 rounded" style={{ backgroundColor: '#F9F9F9' }}>
                    <div className="min-w-0">
                      <p className="text-xs font-medium truncate" style={{ color: '#333' }}>{e.name}</p>
                      <p className="text-xs truncate" style={{ color: '#6B6257' }}>{e.email}</p>
                    </div>
                    <div className="text-right shrink-0 ml-3">
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded" style={{ backgroundColor: st.bg, color: st.fg }}>{st.label}</span>
                      <p className="text-[10px] mt-0.5" style={{ color: '#6B6257' }}>{fmtDate(e.downloaded_at)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WhitePaperLedgerCard;
